import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from '../common/loader';
import BackButton from '../common/backButton';
import { checkAuth, getUserInfo } from '../utils/common';
import moment from 'moment';
import { getDataWithToken, postDataWithToken } from '../api/api';
import { GET_SETTINGS_DATA, OPEN_PIGGY_BANK, PIGGY_BANK_STATUS, PRODUCT_IDS } from '../helpers/Constant';

function PiggyBank() {
    const { t } = useTranslation();
    const [userData, setUserData] = useState(getUserInfo());
    const [loading, setLoading] = useState(true);
    const [userPiggyCoins, setUserPiggyCoins] = useState(userData?.userAccount?.piggyBankAmount || 0);
    const [piggyBankLimit, setPiggyBankLimit] = useState(300);
    const [piggyBankAmount, setPiggyBankAmount] = useState(1);
    const [showRewardModal, setShowRewardModal] = useState(false);
    const [rewardData, setRewardData] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        async function fetchSettingsData() {
            const response = await getDataWithToken(GET_SETTINGS_DATA);

            if (response.piggy_bank_limit) {
                setPiggyBankLimit(parseInt(response.piggy_bank_limit));
            }
            if (response.piggy_bank_opening_price) {
                setPiggyBankAmount(parseInt(response.piggy_bank_opening_price));
            }
            setLoading(false);
        }
        fetchSettingsData();
    }, []);
    const [remainingTime, setRemainingTime] = useState('00:00:00');

    const calculateRemainingTime = () => {
        const now = moment();
        const nextReset = moment().clone().add(1, 'day').startOf('day');
        const remainingTimeDiff = Math.max(0, nextReset.diff(now));
        const d = moment.duration(remainingTimeDiff);
        const hours = Math.floor(d.asHours());
        const minutes = d.minutes();
        const seconds = d.seconds();
        return [hours, minutes, seconds].map((n) => String(n).padStart(2, '0')).join(':');
    };
    useEffect(() => {
        setRemainingTime(calculateRemainingTime());
        const timer = setInterval(() => {
            setRemainingTime(calculateRemainingTime());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const handleSmashIt = async () => {
        setErrorMessage("");
        if(userPiggyCoins < piggyBankLimit) return false;

        setLoading(true);
        const response = await getDataWithToken(PIGGY_BANK_STATUS);
        if(!response?.availableToOpen || (response?.status && response?.status !== 200)){
            setErrorMessage(response?.message || "Something went wrong. Please try again.");
            setLoading(false);
            return false;
        }
        if(piggyBankAmount > 0){
            const url = `uniwebview://buyRequest?itemBundleID=${PRODUCT_IDS.PIGGY_BANK}&amount=${piggyBankAmount}`;
            window.location.href = url;
        } else {
            const response = await postDataWithToken(OPEN_PIGGY_BANK, { purchaseToken: 'Free', platform: '' });
            if(response?.data?.reward){
                setRewardData(response.data.reward);
                setShowRewardModal(true);
                setUserPiggyCoins(parseInt(response.data.remainingPiggyBankAmount));
                refreshUserData();
            } else {
                setErrorMessage(response?.message || "Something went wrong. Please try again.");
            }
        }
        setLoading(false);
    }

    window.buyRequestSuccess = async (payloadOrToken, platformArg) => {
        let data = payloadOrToken;
        if (typeof data === 'string') {
            try {
                data = JSON.parse(data);
            } catch {
                setLoading(false);
            }
        }
        
        const isObjectPayload = data && typeof data === 'object' && !Array.isArray(data);
        const purchaseToken = isObjectPayload ? (data.token ?? data.purchaseToken) : data;
        const platform = isObjectPayload ? data.platform : platformArg;

        if (purchaseToken != null && platform != null) {
            const response = await postDataWithToken(OPEN_PIGGY_BANK, { purchaseToken: purchaseToken, platform: platform });
            if(response?.data?.reward){
                setRewardData(response.data.reward);
                setShowRewardModal(true);
                setUserPiggyCoins(parseInt(response.data.remainingPiggyBankAmount));
                refreshUserData();
            } else {
                setErrorMessage(response?.message || "Something went wrong. Please try again.");
            }
        }
        setLoading(false);
    }

    window.buyRequestFailed = (itemBundleID) => {
        setErrorMessage("Your payment failed. Please try again.");
        setTimeout(() => {
            setErrorMessage("");
        }, 3000);
        setLoading(false);
    }

    const handleCloseRewardModal = () => {
        setShowRewardModal(false);
        setRewardData(null);
    }

    const refreshUserData = async () => {
        const response = await checkAuth();
        if(response){
            setUserData(response);
            setUserPiggyCoins(parseInt(response.userAccount.piggyBankAmount));
        }
    }

    return (
        <>
            <div className='wrapper h-100'>
                {loading && <Loader />}

                <div className='mt-2 position-relative z-1'>
                    <BackButton label={t('Home')} />
                </div>

                <div className='piggy_bank_wrapper text-center'>
                    <div className='piggy_bank_heading_wrapper position-relative pb-1'>
                        <div className='piggy_bank_heading position-relative z-1'>
                            <h1 className='piggy_bank_title text-uppercase fw-bold'>{t('Piggy Bank')}</h1>
                            <img src="../images/piggy-bank.png" alt="piggy-bank" />
                        </div>
                        <div className='piggy_bank_round_wrapper position-absolute top-50 start-50 translate-middle'>
                            <div className='piggy_bank_round piggy_bank_round_1 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                            <div className='piggy_bank_round piggy_bank_round_2 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                            <div className='piggy_bank_round piggy_bank_round_3 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                            <div className='piggy_bank_round piggy_bank_round_4 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                        </div>
                    </div>
                    <div className='piggy_bank_count_box position-relative mx-auto mt-2'>
                        <div className='piggy_bank_count fw-bold text-start'>
                            <span className='text-gold'>{userPiggyCoins}</span>
                            <span className='text-white'>/{piggyBankLimit}</span>
                        </div>
                        <div className='piggy_bank_progressbar_track position-relative w-100'>
                            <div className='piggy_bank_progressbar_thumb position-absolute start-0 top-0 bottom-0 mw-100 rounded-1' style={{ width: `${isNaN(userPiggyCoins / piggyBankLimit * 100) ? 0 : Math.round(userPiggyCoins / piggyBankLimit * 100)}%` }}></div>
                        </div>
                    </div>
                    <div className='piggy_bank_timer fw-light text-center'>Resets in {remainingTime}</div>
                    <div className='piggy_bank_feedback fw-medium px-3'>Collect {piggyBankLimit} Sweat Coins to unlock an exclusive reward.</div>
                    <div className='piggy_bank_feedback fw-medium px-3 d-none'>Congratulations! <br/>Claim your exclusive reward.</div>

                    {errorMessage && <div className='piggy_bank_error error_message text-center mb-3'>{errorMessage}</div>}

                    <div className='piggy_bank_btn'>
                        <button className='cta_button bg-warning text-dark fw-bold text-uppercase' onClick={handleSmashIt} disabled={userPiggyCoins < piggyBankLimit}>
                            <span className='text-dark'>Smash it {piggyBankAmount > 0 ? `for ${piggyBankAmount}` : ''}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Reward Modal */}
            { (rewardData) && (<>
                {(showRewardModal) && <div className='modal-backdrop fade show'></div>}
                <div className={`modal show ${showRewardModal ? 'show' : ''}`} style={{ display: showRewardModal ? 'block' : 'none' }}>
                    <div className='modal-dialog px-0' role='document'>
                        <div className='modal-content modal_content bg-black congrats_modal'>
                            <div className='modal-body text-center'>
                                <div className='congrats_wrapper position-relative pb-1'>
                                    <div className='congrats_heading position-relative z-1'>
                                        {
                                            (rewardData.key === 'premium_trial_1_day' || rewardData.key === 'premium_discount_70') && (
                                                <div className='congrats_member lh-1 position-absolute w-100'>
                                                    <div className='congrats_member_title fw-bold mb-0'>{t('MUVE ELITE')}</div>
                                                    <div className='congrats_member_role d-inline-block fw-medium rounded-corners text-uppercase align-middle p-1 w-100'>Premium</div>
                                                </div>
                                            )
                                        }
                                        <h2 className='congrats_title text-uppercase fw-bold'>{t('Congratulations!')}</h2>
                                        <div className='congrats_subtitle fw-light'>Your Reward From Smashing PiggyBank</div>
                                        {
                                            (rewardData.key === 'active_points_2x_24h' || rewardData.key === 'active_points_2x_48h' || rewardData.key === 'active_points_2x_3d' || rewardData.key === 'active_points_2x_7d') ? (
                                                <div className='congrats_card d-flex flex-column gap-2 p-1 pb-3 mb-3'>
                                                    <div className='congrats_card_image'>
                                                        <img className='img-fluid' src='../images/reward-2x-points.png' alt='Reward' />
                                                    </div>
                                                    <div className='congrats_card_text d-flex align-items-end justify-content-center'>2X Active Points For <br />{rewardData.meta.durationHours ? `${rewardData.meta.durationHours} Hrs` : `${rewardData.meta.durationDays} Days`}</div>
                                                </div>
                                            ) : null
                                        }
                                        {
                                            (rewardData.key === 'streak_saver_1' || rewardData.key === 'streak_saver_3') ? (
                                                <div className='congrats_card d-flex flex-column gap-2 p-1 pb-3 mb-3'>
                                                    <div className='congrats_card_image'>
                                                        <img className='img-fluid' src='../images/reward-streak-saver.png' alt='Reward' />
                                                    </div>
                                                    <div className='congrats_card_text d-flex align-items-end justify-content-center'>{rewardData.meta.streakSaverAdded} Streak Savers</div>
                                                </div>
                                            ) : null
                                        }
                                        {
                                            (rewardData.key === 'premium_trial_1_day') ? (
                                                <div className='congrats_card d-flex flex-column gap-2 p-1 pb-3 mb-3'>
                                                    <div className='congrats_card_image'>
                                                        <img className='img-fluid' src='../images/reward-elite.png' alt='Reward' />
                                                    </div>
                                                    <div className='congrats_card_text d-flex align-items-end justify-content-center'>1 Day Elite</div>
                                                </div>
                                            ) : null
                                        }
                                        {
                                            (rewardData.key === 'premium_discount_70') ? (
                                                <div className='congrats_card d-flex flex-column gap-2 p-1 pb-3 mb-3'>
                                                    <div className='congrats_card_image'>
                                                        <img className='img-fluid' src='../images/reward-discount.png' alt='Reward' />
                                                    </div>
                                                    <div className='congrats_card_text d-flex align-items-end justify-content-center'>70% Discount On Elite</div>
                                                </div>
                                            ) : null
                                        }
                                        {
                                            (rewardData.key === 'active_points_7x_24h') ? (
                                                <div className='congrats_card d-flex flex-column gap-2 p-1 pb-3 mb-3'>
                                                    <div className='congrats_card_image'>
                                                        <img className='img-fluid' src='../images/reward-7x-multiplier.png' alt='Reward' />
                                                    </div>
                                                    <div className='congrats_card_text d-flex align-items-end justify-content-center'>7X Multiplier For <br />24Hrs</div>
                                                </div>
                                            ) : null
                                        }
                                    </div>
                                    <div className='congrats_round_wrapper position-absolute top-50 start-50 translate-middle'>
                                        <div className='congrats_round congrats_round_1 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                        <div className='congrats_round congrats_round_2 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                        <div className='congrats_round congrats_round_3 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                    </div>
                                </div>

                                <button className='cta_button bg-warning border-0 text-uppercase' onClick={handleCloseRewardModal}>Claim</button>
                            </div>
                        </div>
                    </div>
                </div>
            </>)}
        </>
    );
}
export default PiggyBank